import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { EquipmentTypeTemplateDto, EquipmentType } from '../../types';
import { Search, X, Check } from 'lucide-react';
import Modal from '../ui/Modal';
import { formatCurrency } from '../../utils/formatters';

interface EquipmentTemplateBrowserProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectTemplate: (template: EquipmentTypeTemplateDto) => void;
	companyType?: number;
}

const EquipmentTemplateBrowser: React.FC<EquipmentTemplateBrowserProps> = ({
	isOpen,
	onClose,
	onSelectTemplate,
	companyType = 0 // Default to Facilities
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState<string>('all');

	// Fetch templates for company type
	const { data: templatesResponse, isLoading } = useQuery({
		queryKey: ['equipment-templates', companyType],
		queryFn: () => apiService.getTemplatesForCompanyType(companyType),
		enabled: isOpen && companyType !== undefined,
	});

	const templates = templatesResponse?.data || [];

	// Get unique categories
	const categories = ['all', ...Array.from(new Set(templates.map(t => t.category).filter(Boolean)))].filter(Boolean) as string[];

	// Filter templates
	const filteredTemplates = templates.filter(template => {
		const matchesSearch = !searchTerm || 
			template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			template.category?.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
		return matchesSearch && matchesCategory && template.isActive;
	});

	const handleSelectTemplate = (template: EquipmentTypeTemplateDto) => {
		onSelectTemplate(template);
		onClose();
	};

	const getEquipmentTypeDisplay = (type: EquipmentType): string => {
		const typeMap: Record<EquipmentType, string> = {
			[EquipmentType.HVAC]: 'HVAC',
			[EquipmentType.Refrigeration]: 'Refrigeration',
			[EquipmentType.Plumbing]: 'Plumbing',
			[EquipmentType.Electrical]: 'Electrical',
			[EquipmentType.FireSafety]: 'Fire Safety',
			[EquipmentType.Kitchen]: 'Kitchen',
			[EquipmentType.Lighting]: 'Lighting',
			[EquipmentType.BuildingSystems]: 'Building Systems',
			[EquipmentType.Other]: 'Other'
		};
		return typeMap[type] || 'Other';
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Select Equipment Template" maxWidth="6xl">
			<div className="space-y-4">
				{/* Search and Filter */}
				<div className="flex flex-col sm:flex-row gap-4">
					<div className="flex-1 relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
						<input
							type="text"
							placeholder="Search templates..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-color focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
						/>
					</div>
					<select
						value={selectedCategory}
						onChange={(e) => setSelectedCategory(e.target.value)}
						className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-color focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
					>
						{categories.map(cat => (
							<option key={cat} value={cat}>
								{cat === 'all' ? 'All Categories' : cat}
							</option>
						))}
					</select>
				</div>

				{/* Templates Grid */}
				{isLoading ? (
					<div className="text-center py-12">
						<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-color"></div>
						<p className="mt-2 text-gray-600 dark:text-gray-400">Loading templates...</p>
					</div>
				) : filteredTemplates.length === 0 ? (
					<div className="text-center py-12">
						<p className="text-gray-600 dark:text-gray-400">No templates found</p>
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto">
						{filteredTemplates.map(template => (
							<button
								key={template.id}
								onClick={() => handleSelectTemplate(template)}
								className="text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-accent-color hover:shadow-md transition-all bg-white dark:bg-gray-800"
							>
								<div className="flex items-start justify-between mb-2">
									<div className="flex items-center gap-2">
										{template.icon && <span className="text-2xl">{template.icon}</span>}
										<h3 className="font-semibold text-gray-900 dark:text-gray-100">{template.name}</h3>
									</div>
									<Check className="w-5 h-5 text-accent-color opacity-0 group-hover:opacity-100" />
								</div>
								{template.category && (
									<p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{template.category}</p>
								)}
								<div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
									<div className="flex items-center justify-between">
										<span>Type:</span>
										<span className="font-medium">{getEquipmentTypeDisplay(template.equipmentType)}</span>
									</div>
									{template.expectedLifespanYears && (
										<div className="flex items-center justify-between">
											<span>Lifespan:</span>
											<span className="font-medium">{template.expectedLifespanYears} years</span>
										</div>
									)}
									{(template.typicalReplacementCostMin || template.typicalReplacementCostMax) && (
										<div className="flex items-center justify-between">
											<span>Replacement:</span>
											<span className="font-medium">
												{template.typicalReplacementCostMin && template.typicalReplacementCostMax
													? `${formatCurrency(template.typicalReplacementCostMin)} - ${formatCurrency(template.typicalReplacementCostMax)}`
													: template.typicalReplacementCostMin
													? formatCurrency(template.typicalReplacementCostMin)
													: template.typicalReplacementCostMax
													? formatCurrency(template.typicalReplacementCostMax)
													: 'N/A'}
											</span>
										</div>
									)}
									{template.suggestedPMFrequencyMonths && (
										<div className="flex items-center justify-between">
											<span>PM Frequency:</span>
											<span className="font-medium">
												{template.suggestedPMFrequencyMonths === 1
													? 'Monthly'
													: template.suggestedPMFrequencyMonths === 3
													? 'Quarterly'
													: template.suggestedPMFrequencyMonths === 6
													? 'Bi-annual'
													: template.suggestedPMFrequencyMonths === 12
													? 'Annual'
													: `Every ${template.suggestedPMFrequencyMonths} months`}
											</span>
										</div>
									)}
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		</Modal>
	);
};

export default EquipmentTemplateBrowser;

